import { apiHandler } from "../buyerApiHandler";

/**
 * Theme Studio: festival / season looks for the buyer home top-dash.
 * Buyer API /admin/home-themes (x-api-key, sent by apiHandler).
 *
 * The types and ranges below mirror the Buyer API contract in
 * ONDC-Buyer-API/src/home-theme/theme-config.ts. The server sanitises every
 * config (drops unknown keys, clamps numbers, rejects bad colours / URLs), so
 * keep the ranges here in step with sanitizeThemeConfig().
 */

export const PARTICLE_PRESETS = [
  "none", "snow", "rain", "leaves", "petals", "sparkles", "confetti",
  "fireworks", "embers", "bubbles", "lanterns", "stars",
] as const;
export const GARLAND_PRESETS = ["none", "toran", "lights", "marigold", "bells", "stars", "mango"] as const;
export const EDGE_PRESETS = ["round", "wave", "scallop", "zigzag", "flat"] as const;
export const GREETING_EFFECTS = ["none", "shimmer", "glow", "bounce"] as const;
export const BURST_PRESETS = ["cracker", "confetti", "snow", "hearts", "petals"] as const;
export const LAYER_TYPES = ["lottie", "image", "emoji"] as const;
export const DIRECTIONS = ["vertical", "horizontal", "diagonal"] as const;
export const LAYER_MOTIONS = ["none", "float", "sway", "spin", "pulse", "flyby"] as const;
export const MAX_LAYERS = 12;

export type ParticlePreset = (typeof PARTICLE_PRESETS)[number];
export type GarlandPreset = (typeof GARLAND_PRESETS)[number];
export type EdgePreset = (typeof EDGE_PRESETS)[number];
export type GreetingEffect = (typeof GREETING_EFFECTS)[number];
export type BurstPreset = (typeof BURST_PRESETS)[number];
export type LayerType = (typeof LAYER_TYPES)[number];
export type DashDirection = (typeof DIRECTIONS)[number];
export type LayerMotion = (typeof LAYER_MOTIONS)[number];

export interface ThemeColors {
  dashFrom: string;
  dashTo: string;
  dashVia?: string | null;
  dashDirection: DashDirection;
  onDash: string;
  statusBar: "light" | "dark";
  tabSelectedFill: string;
  tabSelectedRing: string;
  tabSelectedText: string;
  tabFrom: string;
  tabTo: string;
  tabText: string;
  tabUnderline: string;
  tabUnderlineSelected: string;
  searchBg: string;
  searchText: string;
  searchIcon: string;
  micColor: string;
  accent: string;
}

export interface ThemeLayer {
  id: string;
  type: LayerType;
  src: string;
  x: number;
  y: number;
  w: number;
  opacity: number;
  loop: boolean;
  speed: number;
  depth: number;
  every: number;
  motion: LayerMotion;
  front: boolean;
}

export interface ThemeConfig {
  colors: ThemeColors;
  night?: Partial<ThemeColors> | null;
  background: { imageUrl: string | null; opacity: number; repeat: boolean; parallax: number };
  layers: ThemeLayer[];
  particles: {
    preset: ParticlePreset;
    density: number;
    speed: number;
    size: number;
    wind: number;
    colors: string[];
    glyphs: string[];
  };
  garland: { preset: GarlandPreset; colors: string[]; sway: boolean };
  edge: { preset: EdgePreset; size: number };
  greeting: {
    text: string;
    textTa: string;
    color: string;
    effect: GreetingEffect;
    countdownTo: string | null;
  };
  interaction: {
    tapBurst: boolean;
    burst: BurstPreset;
    haptic: boolean;
    soundUrl: string | null;
    tilt: boolean;
  };
  model3d: {
    url: string | null;
    x: number;
    y: number;
    size: number;
    autoRotate: boolean;
    rotateSpeed: number;
  };
}

export const DEFAULT_COLORS: ThemeColors = {
  dashFrom: "#00B14F",
  dashTo: "#00B14F",
  dashVia: null,
  dashDirection: "vertical",
  onDash: "#FFFFFF",
  statusBar: "light",
  tabSelectedFill: "#FFF6EA",
  tabSelectedRing: "rgba(255,255,255,0.42)",
  tabSelectedText: "#0A7A3C",
  tabFrom: "#3DDA83",
  tabTo: "#0A8F43",
  tabText: "#FFFFFF",
  tabUnderline: "#FFD54A",
  tabUnderlineSelected: "#0A7A3C",
  searchBg: "#FFFFFF",
  searchText: "#8A8A8A",
  searchIcon: "#3A3A3A",
  micColor: "#F15A24",
  accent: "#00B14F",
};

export const DEFAULT_CONFIG: ThemeConfig = {
  colors: DEFAULT_COLORS,
  night: null,
  background: { imageUrl: null, opacity: 0.35, repeat: false, parallax: 0.3 },
  layers: [],
  particles: { preset: "none", density: 0.5, speed: 1, size: 1, wind: 0, colors: [], glyphs: [] },
  garland: { preset: "none", colors: [], sway: true },
  edge: { preset: "round", size: 1 },
  greeting: { text: "", textTa: "", color: "#FFFFFF", effect: "none", countdownTo: null },
  interaction: { tapBurst: false, burst: "confetti", haptic: true, soundUrl: null, tilt: false },
  model3d: { url: null, x: 0.85, y: 0.25, size: 0.28, autoRotate: true, rotateSpeed: 1 },
};

/** Slider ranges, identical to the server's clamps. */
export const RANGES = {
  bgOpacity: { min: 0, max: 1, step: 0.01 },
  bgParallax: { min: 0, max: 1, step: 0.01 },
  layerX: { min: -0.5, max: 1.5, step: 0.01 },
  layerY: { min: -0.5, max: 1.5, step: 0.01 },
  layerW: { min: 0.02, max: 1.5, step: 0.01 },
  layerOpacity: { min: 0, max: 1, step: 0.01 },
  layerSpeed: { min: 0.1, max: 4, step: 0.1 },
  layerDepth: { min: 0, max: 1, step: 0.05 },
  layerEvery: { min: 0, max: 600, step: 1 },
  particleDensity: { min: 0, max: 1, step: 0.01 },
  particleSpeed: { min: 0.2, max: 3, step: 0.05 },
  particleSize: { min: 0.4, max: 3, step: 0.05 },
  particleWind: { min: -1, max: 1, step: 0.05 },
  edgeSize: { min: 0.3, max: 2, step: 0.05 },
  modelX: { min: 0, max: 1, step: 0.01 },
  modelY: { min: 0, max: 1, step: 0.01 },
  modelSize: { min: 0.1, max: 0.6, step: 0.01 },
  modelRotateSpeed: { min: 0.1, max: 5, step: 0.1 },
  priority: { min: -100, max: 100, step: 1 },
} as const;

/** Max list lengths the server keeps. */
export const LIMITS = {
  particleColors: 8,
  particleGlyphs: 6,
  garlandColors: 6,
  cities: 30,
  greeting: 60,
  emoji: 16,
  glyph: 8,
  name: 80,
};

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGBA = /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\)$/i;
export const isColor = (v: unknown): v is string =>
  typeof v === "string" && (HEX.test(v.trim()) || RGBA.test(v.trim()));
export const isHttpUrl = (v: unknown): v is string =>
  typeof v === "string" && /^https?:\/\/\S{3,2000}$/i.test(v.trim());

/** Fill gaps so the editor and preview always have every field. */
export const withDefaults = (raw: any): ThemeConfig => {
  const r = raw && typeof raw === "object" ? raw : {};
  const d = DEFAULT_CONFIG;
  return {
    colors: { ...d.colors, ...(r.colors ?? {}) },
    night: r.night && typeof r.night === "object" && Object.keys(r.night).length ? { ...r.night } : null,
    background: { ...d.background, ...(r.background ?? {}) },
    layers: Array.isArray(r.layers)
      ? r.layers.map((l: any, i: number) => ({
          id: `layer-${i + 1}`,
          type: "image",
          src: "",
          x: 0.5,
          y: 0.5,
          w: 0.3,
          opacity: 1,
          loop: true,
          speed: 1,
          depth: 0.5,
          every: 0,
          motion: "none",
          front: false,
          ...l,
        }))
      : [],
    particles: {
      ...d.particles,
      ...(r.particles ?? {}),
      colors: Array.isArray(r.particles?.colors) ? r.particles.colors : [],
      glyphs: Array.isArray(r.particles?.glyphs) ? r.particles.glyphs : [],
    },
    garland: {
      ...d.garland,
      ...(r.garland ?? {}),
      colors: Array.isArray(r.garland?.colors) ? r.garland.colors : [],
    },
    edge: { ...d.edge, ...(r.edge ?? {}) },
    greeting: { ...d.greeting, ...(r.greeting ?? {}) },
    interaction: { ...d.interaction, ...(r.interaction ?? {}) },
    model3d: { ...d.model3d, ...(r.model3d ?? {}) },
  };
};

export type ThemeStatus = "draft" | "scheduled" | "live" | "archived";

export type HomeTheme = {
  id: number;
  name: string;
  slug: string;
  preset_key: string | null;
  status: ThemeStatus;
  is_default: boolean;
  starts_at: string | null;
  ends_at: string | null;
  priority: number;
  cities: string[] | null;
  config: ThemeConfig;
  version: number;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type ThemeList = {
  active_id: number | null;
  presets: Array<{ key: string; name: string }>;
  themes: HomeTheme[];
};

export type AssetKind = "image" | "lottie" | "glb" | "audio" | "video";

export type ThemeAsset = {
  id: number;
  theme_id: number | null;
  kind: AssetKind;
  name: string;
  url: string;
  size_bytes: number | null;
  meta?: Record<string, any>;
  created_at?: string;
};

/** Upload rules per kind (same as the server's). */
export const ASSET_RULES: Record<AssetKind, { label: string; accept: string; maxMb: number }> = {
  image: { label: "Images", accept: ".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif", maxMb: 3 },
  lottie: { label: "Lottie", accept: ".json,.lottie,application/json", maxMb: 2 },
  glb: { label: "3D models", accept: ".glb,model/gltf-binary", maxMb: 8 },
  audio: { label: "Sounds", accept: ".mp3,.m4a,.aac,.wav,.ogg,audio/*", maxMb: 2 },
  video: { label: "Videos", accept: ".mp4,.webm,video/mp4,video/webm", maxMb: 10 },
};

const json = { headers: { "Content-Type": "application/json" } };
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

// The Buyer API wraps bodies in { success, data }.
const unwrap = (res: any) => res?.data ?? res;

const normaliseTheme = (t: any): HomeTheme => ({
  ...t,
  preset_key: t?.preset_key ?? null,
  starts_at: t?.starts_at ?? null,
  ends_at: t?.ends_at ?? null,
  cities: t?.cities ?? null,
  priority: Number(t?.priority) || 0,
  config: withDefaults(t?.config),
});

const normaliseList = (body: any): ThemeList => ({
  active_id: body?.active_id ?? null,
  presets: Array.isArray(body?.presets) ? body.presets : [],
  themes: Array.isArray(body?.themes) ? body.themes.map(normaliseTheme) : [],
});

export const listThemes = async (): Promise<ThemeList> =>
  normaliseList(unwrap(await apiHandler("get", "/admin/home-themes")));

export const getTheme = async (id: number): Promise<HomeTheme> =>
  normaliseTheme(unwrap(await apiHandler("get", `/admin/home-themes/${id}`)));

export const createTheme = async (body: {
  name: string;
  preset_key?: string;
  from_id?: number;
}): Promise<HomeTheme> =>
  normaliseTheme(unwrap(await apiHandler("post", "/admin/home-themes", body, json)));

export type ThemePatch = {
  name?: string;
  config?: ThemeConfig;
  priority?: number;
  cities?: string[] | null;
  starts_at?: string | null;
  ends_at?: string | null;
};

export const updateTheme = async (id: number, body: ThemePatch): Promise<HomeTheme> =>
  normaliseTheme(unwrap(await apiHandler("patch", `/admin/home-themes/${id}`, body, json)));

/** Turns a theme on now. Activating the default theme = "use default". */
export const activateTheme = async (id: number): Promise<ThemeList> =>
  normaliseList(unwrap(await apiHandler("post", `/admin/home-themes/${id}/activate`, {}, json)));

export const scheduleTheme = async (
  id: number,
  body: { starts_at: string; ends_at: string; priority?: number },
): Promise<ThemeList> =>
  normaliseList(unwrap(await apiHandler("post", `/admin/home-themes/${id}/schedule`, body, json)));

export const deactivateTheme = async (id: number): Promise<ThemeList> =>
  normaliseList(unwrap(await apiHandler("post", `/admin/home-themes/${id}/deactivate`, {}, json)));

export const archiveTheme = async (id: number): Promise<ThemeList> =>
  normaliseList(unwrap(await apiHandler("post", `/admin/home-themes/${id}/archive`, {}, json)));

export const resetTheme = async (id: number): Promise<HomeTheme> =>
  normaliseTheme(unwrap(await apiHandler("post", `/admin/home-themes/${id}/reset`, {}, json)));

export const deleteTheme = (id: number) => apiHandler("delete", `/admin/home-themes/${id}`);

export const listAssets = async (kind?: AssetKind): Promise<ThemeAsset[]> => {
  const body = unwrap(
    await apiHandler("get", "/admin/home-themes/assets", undefined, kind ? { params: { kind } } : {}),
  );
  return Array.isArray(body) ? body : [];
};

export const uploadAsset = async (file: File, kind?: AssetKind, themeId?: number): Promise<ThemeAsset> => {
  const fd = new FormData();
  fd.append("file", file);
  if (kind) fd.append("kind", kind);
  if (themeId) fd.append("theme_id", String(themeId));
  return unwrap(await apiHandler("post", "/admin/home-themes/assets", fd, multipart));
};

export const deleteAsset = (assetId: number) =>
  apiHandler("delete", `/admin/home-themes/assets/${assetId}`);
